import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  TextField,
  CircularProgress,
  IconButton,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import NotificationSnackbar from "./NotificationBar";
function EditHwmsCodeModal({ open, onClose, onSave, onSaveSuccess, unit }) {
  const theme = useTheme();
  const [hwmsCode, setHwmsCode] = useState("");
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const handleSnackbarClose = () => setSnackbarOpen(false);
  useEffect(() => {
    if (unit && open) {
      setHwmsCode(unit.hwmsCode ? String(unit.hwmsCode) : "");
    }
  }, [unit, open]);
  const validate = () => {
    const errs = {};
    if (hwmsCode && (isNaN(hwmsCode) || Number(hwmsCode) < 1))
      errs.hwmsCode = "Must be a positive number";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };
  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const res = await fetch(`/api/support/update-hwmscode/${unit.unitId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          hwmsCode: hwmsCode ? Number(hwmsCode) : null,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.message || "Failed to update HWMS code");
      }
      setSnackbarMessage(data?.message || "HWMS Code updated successfully");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setSaved(true);
      setTimeout(() => {
        setSaved(false);
        onSaveSuccess?.();
        onClose();
      }, 1200);
    } catch (err) {
      console.error("Save error:", err);
      setSnackbarMessage(err.message || "Something went wrong");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setSaving(false);
    }
  };
  const handleClose = () => {
    setErrors({});
    setHwmsCode("");
    setSaved(false);
    onClose();
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: alpha(theme.palette.primary.light, 0.08),
      "& fieldset": {
        borderColor: alpha(theme.palette.primary.light, 0.5),
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.light,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-disabled": {
        backgroundColor: alpha(theme.palette.action.disabled, 0.05),
      },
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: theme.palette.primary.main,
    },
  };

  const readOnlyFieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: alpha(theme.palette.action.disabled, 0.05),
      "& fieldset": {
        borderColor: alpha(theme.palette.primary.light, 0.3),
      },
    },
    "& .MuiInputBase-input.Mui-disabled": {
      WebkitTextFillColor: theme.palette.text.primary,
      fontWeight: 500,
    },
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      sx={{ backgroundColor: "rgba(0, 0, 0, 0.7)" }}
      PaperProps={{
        sx: {
          borderRadius: "12px",
          overflow: "hidden",
          border: `1px solid ${alpha(theme.palette.primary.light, 0.5)}`,
        },
      }}
    >
      {/* ── Colored Header ── */}
      <Box
        sx={{ bgcolor: theme.palette.primary.light, px: 2.5, pt: 2.5, pb: 2 }}
      >
        <Box
          display="flex"
          alignItems="flex-start"
          justifyContent="space-between"
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1.5,
                bgcolor: "rgba(255,255,255,0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <EditIcon sx={{ fontSize: 16, color: "#fff" }} />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontSize: 15,
                  fontWeight: 500,
                  color: "#fff",
                  lineHeight: 1.3,
                }}
              >
                Edit HWMS code
              </Typography>
              <Typography
                sx={{ fontSize: 12, color: "rgba(255,255,255,0.7)", mt: 0.3 }}
              >
                Link IPPIS Unit with HWMS
              </Typography>
            </Box>
          </Box>

          <IconButton
            size="small"
            onClick={handleClose}
            sx={{
              color: "rgba(255,255,255,0.8)",
              border: "1px solid rgba(255,255,255,0.3)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.15)" },
            }}
          >
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>

        {/* Current value pill */}
        {unit?.hwmsCode && (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              mt: 1.5,
              px: 1.5,
              py: 0.75,
              borderRadius: "20px",
              bgcolor: "rgba(255,255,255,0.18)",
              border: "1px solid rgba(255,255,255,0.3)",
            }}
          >
            <Box
              sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#fff" }}
            />
            <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.65)" }}>
              Current:
            </Typography>
            <Typography sx={{ fontSize: 12, fontWeight: 500, color: "#fff" }}>
              {unit.hwmsCode}
            </Typography>
            <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.3)" }}>
              ·
            </Typography>
            <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.7)" }}>
              {unit.unitName}
            </Typography>
          </Box>
        )}
      </Box>

      {/* ── Form Body ── */}
      <DialogContent sx={{ pt: 2.5, pb: 1, bgcolor: "#fff" }}>
        <Box display="flex" flexDirection="column" gap={2}>
          {/* Entity — read-only */}
          <TextField
            label="Entity"
            value={unit?.Entity || ""}
            size="small"
            fullWidth
            disabled
            sx={readOnlyFieldSx}
          />

          {/* Unit Name — read-only */}
          <TextField
            label="Unit"
            value={unit?.unitName || ""}
            size="small"
            fullWidth
            disabled
            sx={readOnlyFieldSx}
          />

          {/* Divider with label */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                flex: 1,
                height: "1px",
                bgcolor: alpha(theme.palette.primary.light, 0.4),
              }}
            />
            <Typography sx={{ fontSize: 11, color: "text.disabled", px: 0.5 }}>
              HWMS code
            </Typography>
            <Box
              sx={{
                flex: 1,
                height: "1px",
                bgcolor: alpha(theme.palette.primary.light, 0.4),
              }}
            />
          </Box>

          {/* HWMS Code */}
          <TextField
            label="HWMS code"
            type="number"
            size="small"
            fullWidth
            value={hwmsCode}
            onChange={(e) => {
              setHwmsCode(e.target.value);
              setErrors((prev) => ({ ...prev, hwmsCode: "" }));
            }}
            error={!!errors.hwmsCode}
            helperText={errors.hwmsCode || "Enter HWMS Code"}
            inputProps={{ min: 1 }}
            sx={fieldSx}
          />
        </Box>
      </DialogContent>

      {/* ── Footer ── */}
      <Box
        sx={{ height: "1px", bgcolor: alpha(theme.palette.primary.light, 0.4) }}
      />
      <DialogActions
        sx={{
          px: 2.5,
          py: 1.75,
          gap: 1,
          bgcolor: alpha(theme.palette.primary.light, 0.08),
        }}
      >
        <Button
          onClick={handleClose}
          variant="outlined"
          size="small"
          sx={{
            textTransform: "none",
            borderColor: alpha(theme.palette.primary.light, 0.5),
            color: "text.secondary",
            "&:hover": {
              bgcolor: alpha(theme.palette.primary.light, 0.1),
              borderColor: theme.palette.primary.light,
            },
          }}
        >
          Cancel
        </Button>

        <Button
          onClick={handleSave}
          variant="contained"
          size="small"
          disabled={saving || saved}
          startIcon={
            saved ? (
              <CheckIcon sx={{ fontSize: 14 }} />
            ) : saving ? (
              <CircularProgress size={13} color="inherit" />
            ) : null
          }
          sx={{
            textTransform: "none",
            minWidth: 115,
            bgcolor: saved
              ? theme.palette.primary.main
              : theme.palette.primary.light,
            "&:hover": { bgcolor: theme.palette.primary.main },
            "&.Mui-disabled": {
              bgcolor: alpha(theme.palette.primary.light, 0.4),
            },
          }}
        >
          {saved ? "Saved!" : saving ? "Saving..." : "Save changes"}
        </Button>
      </DialogActions>
      <NotificationSnackbar
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </Dialog>
  );
}

export default EditHwmsCodeModal;
